// Angular Files
import { Injectable } from '@angular/core';
import { PaymentMethodDto } from '../api/CoreWebApi';
import { PaymentMethodTypeEnum, PaymentMethodTypeEnumConvertor } from '../PaymentMethodTypeEnum';

@Injectable({
    providedIn: 'root'
})
export class TellerOnlinePaymentMethodsService {
    // The brands packed into the merchant-logos svgIcon. Every other card resolves to
    // 'creditCard', which has no brand mark of its own.
    private CARD_TYPES = ['visa', 'mastercard', 'amex', 'discover'];

    /**
     * Canonical card-brand detection for the whole app — call this rather than re-implementing
     * the match. Processor-supplied card type and reference strings vary in case, spacing and
     * abbreviation, so brands are matched on lower-cased fragments.
     * @param cardType String representing the type of a credit card.
     * @returns One of the supported merchant brands, or 'creditCard' when the brand is not
     *          one of them (including when no card type was supplied).
     */
    public checkCardType(cardType: string): string {
        const value = cardType?.toLowerCase() ?? '';

        if (value.includes('visa')) {
            return 'visa';
        }

        if (value.includes('mastercard') || value.includes('mc') || value.includes('m/c')) {
            return 'mastercard';
        }

        if (value.includes('disc') || value.includes('dcvr')) {
            return 'discover';
        }

        if (value.includes('american') || value.includes('amex')) {
            return 'amex';
        }

        return 'creditCard';
    }

    /**
     *
     * @param cardType String representing the type of a credit card.
     * @returns true if the card type matches one of the supported merchants.
     */
    public cardTypeIsMerchant(cardType: string): boolean {
        return this.CARD_TYPES.includes(cardType);
    }

    /**
     * @param paymentMethod Payment method model which the icon is provided for.
     * @returns Name of the svg asset to use for the icon.
     */
    public getPaymentIcon(paymentMethod: PaymentMethodModel): string {
        const paymentMethodType = PaymentMethodTypeEnumConvertor
            .fromDto(paymentMethod.paymentMethodType);

        if (paymentMethodType === PaymentMethodTypeEnum.CreditCard) {
            return this.getPaymentIconFromString(paymentMethod.cardType);
        }

        if (paymentMethodType === PaymentMethodTypeEnum.ECheck) {
            return 'checkbook';
        }

        return 'credit_card';
    }

    /**
     * Used to find the appropriate payment method icon. This works even when the whole method
     * is not present (just a string)
     * @param paymentMethodString Either the reference or card type of a given payment method.
     * @returns String indicating icon to use.
     */
    public getPaymentIconFromString(paymentMethodString: string): string {
        if (this.cardTypeIsMerchant(this.checkCardType(paymentMethodString))) {
            return 'merchant-logos';
        }

        const value = paymentMethodString?.toLowerCase() ?? '';

        if (value.includes('echeck') || value.includes('e-check')) {
            return 'account_balance';
        }

        return 'credit_card';
    }

    /**
     * @param cardType String representing the type of a credit card.
     * @returns The merchant-logo brand modifier class (e.g. 'merchant-logo--visa'), or '' when
     *          the card type isn't one of the supported merchant brands.
     */
    public getMerchantLogoClass(cardType: string): string {
        const brand = this.checkCardType(cardType);
        return this.cardTypeIsMerchant(brand) ? `merchant-logo--${brand}` : '';
    }
}

export class PaymentMethodModel extends PaymentMethodDto {
    type?: PaymentMethodTypeEnum;
}
